import {FC, useMemo, useState} from 'react'

import {TrashIcon} from '@/components/common/icons/TrashIcon'
import {FORM_DROPZONE_HEIGHT} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {formatFileSize} from '@/utils/document/helpers'

import {Text, TextFontWeight, TextVariant} from '../content/Text'
import {FileIcon} from '../icons/FileIcon'
import {FilePreviewModal} from '../modal/FilePreviewModal'
import {ModalWrapper} from '../modal/ModalWrapper'

type UploadedFilePreviewProps = {
  file: File
  removeFile: () => void
}

export const UploadedFilePreview: FC<React.PropsWithChildren<UploadedFilePreviewProps>> = ({
  file,
  removeFile,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const isImage = file.type.includes('image')

  const uploadedFile = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage])

  return (
    <>
      <div className="preview">
        {isImage && uploadedFile ? (
          <img
            className="thumbnail"
            src={uploadedFile}
            alt="thumbnail"
            role="presentation"
            onClick={() => setIsModalVisible(true)}
          />
        ) : (
          <div className="wrapper">
            <FileIcon />
            <div className="content">
              <Text
                variant={TextVariant.text14}
                fontWeight={TextFontWeight.medium}
                className="truncated"
              >
                {file.name}
              </Text>
              <div className="size">
                <Text
                  variant={TextVariant.caption}
                  className="text-block"
                  color={theme.palette.text.light}
                >
                  {formatFileSize(file.size)}
                </Text>
              </div>
            </div>
          </div>
        )}
        <button type="button" onClick={() => removeFile()} className="button-wrapper">
          <TrashIcon color={theme.palette.text.default} />
        </button>
      </div>
      <ModalWrapper
        isVisible={isImage && isModalVisible}
        props={uploadedFile ? {uploadedFile, closeModal: () => setIsModalVisible(false)} : null}
      >
        {(props) => <FilePreviewModal {...props} />}
      </ModalWrapper>

      <style jsx>{`
        .preview {
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          height: ${FORM_DROPZONE_HEIGHT};
          padding: ${theme.spacing[3]};
          border: 1px solid ${theme.palette.gray[200]};
          background-color: ${theme.palette.background.default};
          border-radius: 12px;
          cursor: default;
        }
        .thumbnail {
          display: block;
          height: 100%;
          width: 100%;
          max-width: calc(100% - 40px);
          object-fit: contain;
          cursor: pointer;
        }
        .wrapper {
          display: flex;
          width: 100%;
        }
        .content {
          width: calc(100% - ${theme.spacing[7]});
          margin-left: calc(${theme.spacing[3]} - ${theme.spacing[1]});
        }
        .size {
          margin-top: ${theme.spacing[1]};
        }
        .button-wrapper {
          position: absolute;
          top: ${theme.spacing[2]};
          right: ${theme.spacing[2]};
          padding: ${theme.spacing[2]};
        }
      `}</style>
    </>
  )
}
